/**
 * guidedTourScroll — a one-bit coordination flag between the GuidedTour engine
 * and useScrollResetOnRoute.
 *
 * Why this exists
 * ---------------
 * The tour navigates to a step's route and then scrolls the step's target into
 * the middle of the viewport (GuidedTour's poll → scrollIntoView). Every route
 * change ALSO wakes useScrollResetOnRoute, which resets the page to the top and,
 * a couple of frames later, runs a post-paint reset that re-snaps to the top if
 * anything has scrolled away. On a slow device that post-paint pass lands AFTER
 * the tour has scrolled its target into view, so it drags the page back to the
 * top and the tour card ends up floating over the top of the page (e.g. the
 * Settings Account section) instead of the control it is describing (the Dex
 * visibility toggles).
 *
 * The tour owns its own scroll position while it is open, so the reset must
 * stand down. A module-level boolean (not React state / context) keeps the
 * generic scroll hook decoupled from the session and testable in isolation.
 */
let active = false;

/** Called by the GuidedTour engine: true while the tour is open, false once it
 *  closes. While true, useScrollResetOnRoute skips its post-paint reset so the
 *  tour's scrollIntoView is the last word on scroll position. */
export function setGuidedTourScrollActive(value: boolean): void {
  active = value;
}

/** True while the guided tour is driving scroll and the route-change reset
 *  should not fight it. */
export function isGuidedTourScrollActive(): boolean {
  return active;
}

/**
 * A timed hold for any OTHER page that scrolls itself right after a route
 * change (2026-09-07: the Profile → Calendar event hand-off). The hand-off
 * arms the hold before navigating; for its duration the route reset's
 * post-paint pass, late timers and 2 s scroll-event guard all stand down,
 * exactly as they do for the tour. Without it the guard re-zeroed the page
 * on every frame of the Calendar's scroll: a tug-of-war the trainer saw as a
 * laggy crawl that never landed, and the dozens of post-paint programmatic
 * scrolls left WKWebView's document layer blank (only the promoted sticky
 * filter row kept painting). The synchronous reset AT navigation still runs,
 * so the new page still starts from the top before the hand-off scrolls.
 */
let holdUntil = 0;

export function holdRouteScrollReset(ms: number): void {
  const until = Date.now() + ms;
  if (until > holdUntil) holdUntil = until;
}

export function releaseRouteScrollReset(): void {
  holdUntil = 0;
}

/** True while the guided tour OR a timed hold owns the scroll position. */
export function isRouteScrollResetHeld(): boolean {
  return active || Date.now() < holdUntil;
}
